import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuthContext } from '../../../context/AuthContext';
import { SampleStatusBadges } from '../../components/common/SampleStatusBadges';
import { TagEditor } from '../../components/common/TagEditor';
import { LABEL_SOURCE_STYLES } from '../../constants';
import { EvalApiService } from '../../services/EvalApiService';
import { EvalGtDataset, EvalGtSample, isSampleReady, LABEL_SOURCE_LABELS } from '../../types';
import { fmtIso } from '../../utils';

function countBy<T>(
  items: T[],
  key: (item: T) => string | number | null | undefined
): Array<{ label: string; count: number }> {
  const map = new Map<string, number>();
  for (const item of items) {
    const k = String(key(item) ?? '—');
    map.set(k, (map.get(k) ?? 0) + 1);
  }
  return Array.from(map.entries())
    .map(([label, count]) => ({ label, count }))
    .sort((a, b) => b.count - a.count);
}

const DistributionBar: React.FC<{
  title: string;
  entries: Array<{ label: string; count: number }>;
  total: number;
}> = ({ title, entries, total }) => (
  <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, flexWrap: 'wrap' }}>
    <span
      style={{ fontSize: 11, color: 'var(--text-tertiary)', fontWeight: 600, whiteSpace: 'nowrap' }}
    >
      {title}
    </span>
    {entries.map(({ label, count }) => (
      <span
        key={label}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 3,
          fontSize: 11,
          fontFamily: 'var(--font-mono)',
          background: 'var(--surface-raised)',
          border: '1px solid var(--border)',
          borderRadius: 4,
          padding: '1px 6px',
          color: 'var(--text-secondary)',
        }}
      >
        {label}
        <span style={{ color: 'var(--text-tertiary)' }}>×</span>
        <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{count}</span>
        <span style={{ color: 'var(--text-tertiary)', fontSize: 10 }}>
          ({Math.round((count / total) * 100)}%)
        </span>
      </span>
    ))}
  </div>
);

export const DatasetDetailPage: React.FC = () => {
  const { datasetId } = useParams<{ datasetId: string }>();
  const { token } = useAuthContext();
  const navigate = useNavigate();

  const [dataset, setDataset] = useState<EvalGtDataset | null>(null);
  const [inferences, setInferences] = useState<EvalGtSample[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'samples' | 'add'>('samples');
  const [locking, setLocking] = useState(false);

  const [unassigned, setUnassigned] = useState<EvalGtSample[]>([]);
  const [loadingUnassigned, setLoadingUnassigned] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [adding, setAdding] = useState(false);
  const [filterStoreId, setFilterStoreId] = useState('');
  const [filterChainId, setFilterChainId] = useState('');
  const [filterIngestedBy, setFilterIngestedBy] = useState('');

  useEffect(() => {
    if (!datasetId) return;
    Promise.all([
      EvalApiService.getDataset(token ?? '', datasetId),
      EvalApiService.listGtSamples(token ?? '', { dataset_id: datasetId }),
    ]).then(([ds, { items }]) => {
      setDataset(ds);
      setInferences(items);
      setLoading(false);
    });
  }, [datasetId, token]);

  useEffect(() => {
    if (activeTab !== 'add') return;
    setLoadingUnassigned(true);
    const storeId = filterStoreId ? Number(filterStoreId) : undefined;
    const chainId = filterChainId ? Number(filterChainId) : undefined;
    EvalApiService.listGtSamples(token ?? '', {
      unassigned: true,
      store_id: storeId,
      chain_id: chainId,
      ingested_by: filterIngestedBy || undefined,
    })
      .then(({ items }) => setUnassigned(items))
      .finally(() => setLoadingUnassigned(false));
  }, [activeTab, token, filterStoreId, filterChainId, filterIngestedBy]);

  const handleAddSelected = async () => {
    if (!datasetId || selected.size === 0) return;
    setAdding(true);
    await EvalApiService.addInferencesToDataset(token ?? '', datasetId, {
      gt_sample_ids: Array.from(selected),
    });
    const added = unassigned.filter((i) => selected.has(i.gt_sample_id));
    setInferences((prev) => [...prev, ...added]);
    setUnassigned((prev) => prev.filter((i) => !selected.has(i.gt_sample_id)));
    setSelected(new Set());
    setAdding(false);
  };

  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleLock = async () => {
    if (!dataset) return;
    setLocking(true);
    const updated = await EvalApiService.lockDataset(token ?? '', dataset.dataset_id);
    setDataset(updated);
    setLocking(false);
  };

  if (loading) {
    return (
      <div className="page-content">
        <div style={{ color: 'var(--text-secondary)', fontSize: 13 }}>Loading...</div>
      </div>
    );
  }

  if (!dataset) {
    return (
      <div className="page-content">
        <div style={{ color: 'var(--danger)', fontSize: 13 }}>Dataset not found.</div>
      </div>
    );
  }

  return (
    <div className="page-content">
      <div className="eval-page-header">
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div className="eval-page-title">{dataset.dataset_name}</div>
            <span
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: dataset.status === 'locked' ? 'var(--positive)' : 'var(--warning)',
              }}
            >
              {dataset.status.toUpperCase()}
            </span>
            <span
              style={{
                fontSize: 12,
                color: 'var(--text-secondary)',
                fontFamily: 'var(--font-mono)',
              }}
            >
              {dataset.sample_count} samples
            </span>
          </div>
          {dataset.description && <div className="eval-page-sub">{dataset.description}</div>}
          <div style={{ marginTop: 8 }}>
            <TagEditor
              token={token ?? ''}
              tags={dataset.tags}
              disabled={dataset.status === 'locked'}
              onChange={async (tags) => {
                const updated = await EvalApiService.patchDataset(token ?? '', dataset.dataset_id, {
                  tags,
                });
                setDataset(updated);
              }}
            />
          </div>
        </div>
        {dataset.status === 'draft' && (
          <button className="eval-btn" onClick={handleLock} disabled={locking}>
            {locking ? 'Locking...' : 'Lock Dataset'}
          </button>
        )}
      </div>

      <div className="eval-tabs">
        <button
          className={`eval-tab ${activeTab === 'samples' ? 'active' : ''}`}
          onClick={() => setActiveTab('samples')}
        >
          Samples ({inferences.length})
        </button>
        <button
          className={`eval-tab ${activeTab === 'add' ? 'active' : ''}`}
          onClick={() => setActiveTab('add')}
        >
          Add Samples
        </button>
      </div>

      {activeTab === 'samples' && inferences.length > 0 && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
            marginBottom: 16,
            padding: '10px 12px',
            background: 'var(--surface-raised)',
            border: '1px solid var(--border)',
            borderRadius: 6,
          }}
        >
          <DistributionBar
            title="Chains:"
            entries={countBy(inferences, (i) => i.chain_id)}
            total={inferences.length}
          />
          <DistributionBar
            title="Stores:"
            entries={countBy(inferences, (i) => i.store_id)}
            total={inferences.length}
          />
        </div>
      )}

      {activeTab === 'samples' && (
        <div className="eval-inference-grid">
          {inferences.map((inf) => (
            <div
              key={inf.gt_sample_id}
              className="eval-inference-thumb"
              onClick={() => navigate(`/eval/gt-samples/${inf.gt_sample_id}`)}
            >
              <div className="eval-inference-thumb-img">
                {inf.image_url ? (
                  <img
                    src={inf.image_url}
                    alt={`image_id: ${inf.image_id}`}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                ) : inf.image_id != null ? (
                  `image_id: ${inf.image_id}`
                ) : (
                  inf.gt_sample_id
                )}
              </div>
              <div className="eval-inference-thumb-meta">
                <div style={{ marginBottom: 3 }}>
                  store_id:{' '}
                  <span style={{ fontFamily: 'var(--font-mono)' }}>{inf.store_id ?? '—'}</span>
                </div>
                <div
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                >
                  <span style={LABEL_SOURCE_STYLES[inf.label_source]}>
                    {LABEL_SOURCE_LABELS[inf.label_source]}
                  </span>
                  <button
                    className="eval-btn"
                    style={{ padding: '2px 7px', fontSize: 10 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      window.open(`/gt/audit/${inf.gt_sample_id}`, '_blank');
                    }}
                  >
                    Edit
                  </button>
                </div>
              </div>
            </div>
          ))}
          {inferences.length === 0 && (
            <div style={{ color: 'var(--text-secondary)', fontSize: 13, gridColumn: '1/-1' }}>
              No samples in this dataset yet.
            </div>
          )}
        </div>
      )}

      {activeTab === 'add' && (
        <div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              marginBottom: 12,
              flexWrap: 'wrap',
            }}
          >
            <input
              className="eval-form-input"
              placeholder="store_id"
              value={filterStoreId}
              onChange={(e) => setFilterStoreId(e.target.value.replace(/\D/g, ''))}
              style={{ width: 100, fontFamily: 'var(--font-mono)', fontSize: 12 }}
            />
            <input
              className="eval-form-input"
              placeholder="chain_id"
              value={filterChainId}
              onChange={(e) => setFilterChainId(e.target.value.replace(/\D/g, ''))}
              style={{ width: 100, fontFamily: 'var(--font-mono)', fontSize: 12 }}
            />
            <input
              className="eval-form-input"
              placeholder="ingested by"
              value={filterIngestedBy}
              onChange={(e) => setFilterIngestedBy(e.target.value)}
              style={{ width: 160, fontSize: 12 }}
            />
            <div style={{ marginLeft: 'auto', fontSize: 13, color: 'var(--text-secondary)' }}>
              {loadingUnassigned ? 'Loading…' : `${unassigned.length} unassigned`}
            </div>
            {selected.size > 0 && (
              <button className="eval-btn" onClick={handleAddSelected} disabled={adding}>
                {adding ? 'Adding…' : `Add Selected (${selected.size})`}
              </button>
            )}
          </div>
          <div className="eval-inference-grid">
            {unassigned.map((inf) => {
              const ready = isSampleReady(inf);
              return (
                <div
                  key={inf.gt_sample_id}
                  className={`eval-inference-thumb${selected.has(inf.gt_sample_id) ? ' selected' : ''}`}
                  onClick={() => ready && toggleSelect(inf.gt_sample_id)}
                  style={{
                    cursor: ready ? 'pointer' : 'not-allowed',
                    opacity: ready ? 1 : 0.5,
                    outline: selected.has(inf.gt_sample_id) ? '2px solid var(--accent)' : undefined,
                  }}
                >
                  <div className="eval-inference-thumb-img">
                    {inf.image_url ? (
                      <img
                        src={inf.image_url}
                        alt={`image_id: ${inf.image_id}`}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : inf.image_id != null ? (
                      `image_id: ${inf.image_id}`
                    ) : (
                      inf.gt_sample_id
                    )}
                  </div>
                  <div className="eval-inference-thumb-meta">
                    <div style={{ marginBottom: 2, fontFamily: 'var(--font-mono)', fontSize: 11 }}>
                      store: {inf.store_id ?? '—'} · chain: {inf.chain_id ?? '—'}
                    </div>
                    <div style={{ fontSize: 10, color: 'var(--text-tertiary)', marginBottom: 2 }}>
                      {fmtIso(inf.ingested_at)}
                    </div>
                    {inf.ingested_by && (
                      <div
                        style={{ fontSize: 10, color: 'var(--text-secondary)', marginBottom: 2 }}
                      >
                        by {inf.ingested_by.split('@')[0]}
                      </div>
                    )}
                    <span style={LABEL_SOURCE_STYLES[inf.label_source]}>
                      {LABEL_SOURCE_LABELS[inf.label_source]}
                    </span>
                    <SampleStatusBadges sample={inf} />
                  </div>
                </div>
              );
            })}
            {!loadingUnassigned && unassigned.length === 0 && (
              <div style={{ color: 'var(--text-secondary)', fontSize: 13, gridColumn: '1/-1' }}>
                No unassigned inferences available.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
